/**
 * My Insights - Profile header: identity attributes (name, BU, manager, squad role, DRI tier, DRI registration).
 * Moved out of the metrics strip since these aren't live performance data.
 */
import React from 'react';
import { Avatar, Box, Typography } from '@mui/material';
import { SEARCE_COLORS } from '../../theme';

export interface MyProfileHeaderProps {
  fullName?: string | null;
  businessUnit?: string | null;
  managerName?: string | null;
  squadRole?: string | null;
  driTier?: number | null;
  driRegistrationStatus?: { is_registered?: boolean } | null;
  driRegistrationLoading?: boolean;
}

interface ProfileAttributeTileProps {
  label: string;
  value: React.ReactNode;
  valueColor?: string;
}

const getInitials = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

const ProfileAttributeTile: React.FC<ProfileAttributeTileProps> = ({ label, value, valueColor }) => (
  <Box
    sx={{
      border: `1px solid ${SEARCE_COLORS.neutral_200}`,
      borderRadius: 1.5,
      bgcolor: SEARCE_COLORS.white,
      px: 2,
      py: 1.25,
      minWidth: 120,
    }}
  >
    <Typography
      variant="caption"
      sx={{
        color: SEARCE_COLORS.textSecondary,
        display: 'block',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        fontWeight: 500,
        lineHeight: 1.2,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 700,
        color: valueColor ?? SEARCE_COLORS.textPrimary,
        lineHeight: 1.3,
        mt: 0.5,
        wordBreak: 'break-word',
      }}
    >
      {value}
    </Typography>
  </Box>
);

const MyProfileHeader: React.FC<MyProfileHeaderProps> = ({
  fullName,
  businessUnit,
  managerName,
  squadRole,
  driTier,
  driRegistrationStatus,
  driRegistrationLoading,
}) => {
  const registrationLabel = driRegistrationLoading
    ? '...'
    : driRegistrationStatus?.is_registered
      ? 'Registered'
      : 'Unregistered';
  const registrationValueColor = driRegistrationLoading
    ? SEARCE_COLORS.textSecondary
    : driRegistrationStatus?.is_registered
      ? SEARCE_COLORS.success_700
      : SEARCE_COLORS.textPrimary;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        flexWrap: 'wrap',
        gap: 2,
        p: 2,
        mb: 2,
        borderRadius: 2,
        border: `1px solid ${SEARCE_COLORS.neutral_200}`,
        backgroundColor: SEARCE_COLORS.background,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Avatar
          sx={{
            width: 48,
            height: 48,
            bgcolor: SEARCE_COLORS.white,
            color: SEARCE_COLORS.textPrimary,
            fontWeight: 700,
            border: `1px solid ${SEARCE_COLORS.neutral_400}`,
          }}
        >
          {fullName ? getInitials(fullName) : '?'}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: SEARCE_COLORS.textPrimary, lineHeight: 1.2 }}>
            {fullName || '...'}
          </Typography>
          {businessUnit && (
            <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
              {businessUnit}
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          borderLeft: { xs: 'none', sm: `1px solid ${SEARCE_COLORS.neutral_200}` },
          pl: { xs: 0, sm: 2 },
        }}
      >
        <ProfileAttributeTile label="Manager" value={managerName || '-'} />
        {squadRole && <ProfileAttributeTile label="Squad Role" value={squadRole} />}
        <ProfileAttributeTile label="DRI Tier" value={driTier ?? '-'} />
        <ProfileAttributeTile
          label="DRI Registration"
          value={registrationLabel}
          valueColor={registrationValueColor}
        />
      </Box>
    </Box>
  );
};

export default MyProfileHeader;
